/**
 * teamCollection 월누계 집계 공통 헬퍼
 *
 * 불변식: 같은 출처(원본 compositeKey)의 monthlyReceipt/monthlyExpense는 월 누적값이므로 MAX,
 * 다른 출처 키는 별도 데이터이므로 SUM — 즉 버킷 총합 = Σ(출처별 MAX).
 *
 * '직전 키 1개 기억' 휴리스틱은 출처 키가 A→B→A로 교차 도착하면 SUM 분기가 재발화해
 * 총액이 이중계상되므로, 버킷마다 출처별 MAX 맵을 유지하고 증가분(delta)만 총합에 더한다.
 * (Financial 거래의 += 합산 분과 공존해야 하므로 총합을 덮어쓰지 않고 증가분 방식 사용)
 */

export interface SourceMaxEntry {
  receipts: number;
  expenses: number;
}

/** 버킷 하나가 유지하는 출처(원본 compositeKey)별 MAX 맵 */
export type SourceMaxMap = Record<string, SourceMaxEntry>;

/**
 * 레코드 1건을 출처별 MAX 맵에 반영하고, 버킷 총합에 더해야 할 증가분을 반환한다.
 * - 새 출처: 값 전체가 증가분 (기존 '다른 출처 최초 SUM' 동작과 동일)
 * - 기존 출처: MAX 갱신분만 증가분 (기존 '같은 출처 MAX' 동작과 동일, 감소·동일 값은 0)
 * 결과적으로 버킷의 비Financial 누계 = Σ(출처별 MAX)가 도착 순서와 무관하게 보장된다.
 */
export function accumulateSourceMax(
  sources: SourceMaxMap,
  sourceKey: string,
  receipts: number,
  expenses: number
): SourceMaxEntry {
  const existing = sources[sourceKey];
  if (!existing) {
    sources[sourceKey] = { receipts, expenses };
    return { receipts, expenses };
  }
  const deltaReceipts = Math.max(0, receipts - existing.receipts);
  const deltaExpenses = Math.max(0, expenses - existing.expenses);
  existing.receipts = Math.max(existing.receipts, receipts);
  existing.expenses = Math.max(existing.expenses, expenses);
  return { receipts: deltaReceipts, expenses: deltaExpenses };
}
